import React, { useState } from "react";
import ModalBox from "@components/ui-kit/ModalBox";
import { TableSettings } from "../model";
import styles from "./styles.module.scss";
import { Checkbox } from "antd";
import classNames from "classnames";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: TableSettings;
  onSettingsChange: (localSettings: TableSettings) => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSettingsChange,
}) => {
  const [localSettings, setLocalSettings] = useState<TableSettings>(settings);

  const handleChannelToggle = (
    channel: keyof TableSettings["visibleChannels"],
  ) => {
    setLocalSettings({
      ...localSettings,
      visibleChannels: {
        ...localSettings.visibleChannels,
        [channel]: !localSettings.visibleChannels[channel],
      },
    });
  };

  const handleMetricToggle = (
    metric: keyof TableSettings["visibleMetrics"],
  ) => {
    setLocalSettings({
      ...localSettings,
      visibleMetrics: {
        ...localSettings.visibleMetrics,
        [metric]: !localSettings.visibleMetrics[metric],
      },
    });
  };

  const handleSaveSettings = () => {
    onSettingsChange(localSettings);
    onClose();
  };

  return (
    <ModalBox isOpen={isOpen} onClose={onClose} title="Table Settings">
      {/* Sales Channels Section */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Sales Channels</h3>
        <div className={styles.checkboxList}>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleChannels.amazonUSA}
              onChange={() => handleChannelToggle("amazonUSA")}
            />
            <span>Amazon USA</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleChannels.shopify}
              onChange={() => handleChannelToggle("shopify")}
            />
            <span>Shopify</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleChannels.tiktokShop}
              onChange={() => handleChannelToggle("tiktokShop")}
            />
            <span>TikTok Shop</span>
          </label>
        </div>
      </div>

      {/* Metrics Section */}
      <div className={classNames(styles.section, styles.metricsSection)}>
        <h3 className={styles.sectionTitle}>Metrics</h3>
        <div className={styles.checkboxList}>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleMetrics.impressions}
              onChange={() => handleMetricToggle("impressions")}
            />
            <span>Impressions</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleMetrics.clicks}
              onChange={() => handleMetricToggle("clicks")}
            />
            <span>Clicks</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleMetrics.orders}
              onChange={() => handleMetricToggle("orders")}
            />
            <span>Orders</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleMetrics.cvr}
              onChange={() => handleMetricToggle("cvr")}
            />
            <span>CVR</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleMetrics.sales}
              onChange={() => handleMetricToggle("sales")}
            />
            <span>Sales</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleMetrics.salesShare}
              onChange={() => handleMetricToggle("salesShare")}
            />
            <span>Sales Share</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleMetrics.spend}
              onChange={() => handleMetricToggle("spend")}
            />
            <span>Spend</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleMetrics.acos}
              onChange={() => handleMetricToggle("acos")}
            />
            <span>ACOS</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleMetrics.roi}
              onChange={() => handleMetricToggle("roi")}
            />
            <span>ROI</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleMetrics.cpc}
              onChange={() => handleMetricToggle("cpc")}
            />
            <span>CPC</span>
          </label>
          <label className={styles.checkboxItem}>
            <Checkbox
              checked={localSettings.visibleMetrics.cac}
              onChange={() => handleMetricToggle("cac")}
            />
            <span>CAC</span>
          </label>
        </div>
      </div>
      <div className={styles.buttons}>
        <button className={styles.cancel} onClick={onClose}>
          Cancel
        </button>
        <button className={styles.save} onClick={handleSaveSettings}>
          Save
        </button>
      </div>
    </ModalBox>
  );
};
